import { Loader2 } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { BACKEND_URL } from '@/utils/apiConfig';

/**
 * Standardized full-screen hero section component
 * Used across all pages for consistent branding
 * Now supports dynamic theming via ThemeContext
 */
const HeroSection = ({ 
  title, 
  subtitle, 
  description,
  backgroundImage,
  backgroundColor = "from-blue-900 via-cyan-800 to-blue-900",
  titleColor = "text-white",
  subtitleColor = "text-cyan-400",
  showScrollArrow = true,
  minHeight = "h-screen",
  pageName = null // Optional: specify page name for theme lookup
}) => {
  const { pageStyles, loading } = useTheme();
  const location = useLocation();
  const [mediaLoading, setMediaLoading] = useState(false);
  
  // Scroll indicator — smooth fade, stays hidden until page change
  const [scrollHidden, setScrollHidden] = useState(false);
  const [scrollOpacity, setScrollOpacity] = useState(1);
  const prevPathRef = useRef(location.pathname);
  
  useEffect(() => {
    // Reset on page change
    if (prevPathRef.current !== location.pathname) {
      setScrollHidden(false);
      setScrollOpacity(1);
      prevPathRef.current = location.pathname;
    }
    
    const handleScroll = () => {
      const y = window.scrollY;
      if (y > 400) {
        setScrollHidden(true);
      } else if (!scrollHidden) {
        const fade = y < 80 ? 1 : Math.max(0, 1 - (y - 80) / 320);
        setScrollOpacity(fade);
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname, scrollHidden]);
  
  // Determine current page from route or prop
  const getCurrentPageName = () => {
    if (pageName) return pageName;
    
    const path = location.pathname;
    if (path === '/') return 'home';
    if (path === '/map') return 'map';
    if (path === '/dive-centers' || path.includes('/dive-center')) return 'dive_centers';
    if (path === '/packages') return 'packages';
    if (path === '/gallery') return 'gallery';
    if (path === '/reviews') return 'reviews';
    if (path === '/contact') return 'contact';
    return 'home'; // fallback
  };
  
  const currentPage = getCurrentPageName();
  const headerTheme = pageStyles[currentPage]?.header;
  
  // Wait for theme to load before showing content - prevents flash of old defaults
  const isThemeLoaded = !loading && headerTheme;
  
  // Use ONLY theme data - props are deprecated fallbacks (only used if theme fails to load after timeout)
  const finalTitle = headerTheme?.title;
  const finalSubtitle = headerTheme?.subtitle;
  const finalDescription = headerTheme?.description;
  
  // Determine background styling from theme ONLY
  const bgType = headerTheme?.background_type || 'gradient';
  const rawBgValue = headerTheme?.background_value || backgroundColor;
  const textColor = headerTheme?.text_color || titleColor;
  
  // Resolve relative URLs (e.g. /api/uploads/preset/...) to absolute
  const bgValue = rawBgValue && rawBgValue.startsWith('/api/') ? `${BACKEND_URL}${rawBgValue}` : rawBgValue;
  
  // Build a fallback gradient from theme accent colors for image/video backgrounds
  const accentPrimary = headerTheme?.accent_color || '#0e7490';
  const accentSecondary = headerTheme?.accent_color_secondary || '#1e3a8a';
  const fallbackGradient = `linear-gradient(135deg, #0A0A14 0%, ${accentSecondary}40 30%, ${accentPrimary}20 60%, #0A0A14 100%)`;
  
  const renderBackground = () => {
    if (bgType === 'image') {
      return (
        <div className="w-full h-full" style={{ background: fallbackGradient }}>
          <img 
            src={bgValue}
            alt={headerTheme?.alt_text || title || 'Hero background'}
            className="w-full h-full object-cover"
            style={{ objectPosition: headerTheme?.media_position || 'center' }}
            onLoadStart={() => setMediaLoading(true)}
            onLoad={() => setMediaLoading(false)}
            onError={(e) => { setMediaLoading(false); e.target.style.display = 'none'; }}
          />
        </div>
      );
    } else if (bgType === 'video') {
      return (
        <div className="w-full h-full" style={{ background: fallbackGradient }}>
          <video
            key={bgValue}
            src={bgValue}
            autoPlay
            loop={headerTheme?.video_loop !== false}
            muted
            playsInline
            className="w-full h-full object-cover"
            style={{ objectPosition: headerTheme?.video_position || 'center' }}
            onLoadStart={() => setMediaLoading(true)}
            onLoadedData={() => setMediaLoading(false)}
            onError={(e) => { setMediaLoading(false); e.target.style.display = 'none'; }}
          >
            <source src={bgValue} type="video/mp4" />
          </video>
        </div>
      );
    } else if (bgType === 'gradient') {
      // Check if it's CSS gradient or Tailwind classes
      const isCSSGradient = bgValue.includes('gradient(');
      const isDarkGradient = bgValue.includes('#0A0A') || bgValue.includes('#1a0a') || bgValue.includes('#0d1f');
      
      if (isCSSGradient) {
        return (
          <div className="w-full h-full" style={{ background: bgValue }}>
            {/* Animated star-like dots for dark themes */}
            {isDarkGradient && (
              <>
                <div className="absolute inset-0 opacity-30" style={{
                  backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(162,133,212,0.15) 1px, transparent 1px), radial-gradient(circle at 80% 70%, rgba(62,201,224,0.1) 1px, transparent 1px), radial-gradient(circle at 50% 50%, rgba(255,255,255,0.05) 1px, transparent 1px)',
                  backgroundSize: '80px 80px, 120px 120px, 40px 40px'
                }} />
                {/* Subtle radial glow from center */}
                <div className="absolute inset-0" style={{
                  background: 'radial-gradient(ellipse at 50% 50%, rgba(162,133,212,0.08) 0%, transparent 70%)'
                }} />
              </>
            )}
            {!isDarkGradient && (
              <div className="absolute inset-0 opacity-20">
                <div className="absolute inset-0" style={{
                  backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1) 1px, transparent 1px)',
                  backgroundSize: '50px 50px'
                }} />
              </div>
            )}
          </div>
        );
      } else {
        return (
          <div className={`w-full h-full bg-gradient-to-br ${bgValue}`}>
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0" style={{
                backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1) 1px, transparent 1px)',
                backgroundSize: '50px 50px'
              }}></div>
            </div>
          </div>
        );
      }
    } else {
      // Solid color
      return <div className="w-full h-full" style={{ backgroundColor: bgValue }} />;
    }
  };
  
  return (
    <section className={`hero-section relative ${minHeight} flex items-center justify-center overflow-hidden`}>
      {/* Background - show loading gradient while theme loads */}
      <div className="absolute inset-0 z-0">
        {loading ? (
          <div className="w-full h-full bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800">
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-white/50 animate-spin" />
            </div>
          </div>
        ) : (
          renderBackground()
        )}
      </div>
      
      {/* Animated wave crests for dark themes */}
      {!loading && (bgType === 'gradient' || bgType === 'image') && headerTheme?.accent_color_secondary && (
        <div className="absolute inset-0 z-[1] pointer-events-none overflow-hidden" aria-hidden="true">
          {/* Bottom wave layer - slow drift */}
          <svg className="absolute bottom-0 left-0 w-[200%] wave-float" viewBox="0 0 2880 200" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ height: '200px', opacity: 0.12 }}>
            <path d="M0,100 C240,160 480,40 720,100 C960,160 1200,40 1440,100 C1680,160 1920,40 2160,100 C2400,160 2640,40 2880,100" 
              stroke={headerTheme.accent_color_secondary} strokeWidth="3" fill="none" />
            <path d="M0,130 C300,70 600,170 900,100 C1200,30 1500,170 1800,100 C2100,30 2400,170 2700,100" 
              stroke={headerTheme.accent_color || '#3EC9E0'} strokeWidth="2.5" fill="none" opacity="0.7" />
            <path d="M0,160 C180,120 360,180 540,140 C720,100 900,170 1080,130 C1260,90 1440,160 1620,120 C1800,80 1980,150 2160,110 C2340,70 2520,140 2700,100 L2880,120" 
              stroke={headerTheme.accent_color_secondary} strokeWidth="2" fill="none" opacity="0.5" />
          </svg>
          {/* Top tentacle accent — subtle curling shape */}
          <svg className="absolute top-0 right-0 opacity-[0.03]" width="400" height="400" viewBox="0 0 400 400" fill="none">
            <path d="M400,0 C380,60 350,80 370,140 C390,200 340,240 360,300 C380,360 330,390 350,400" 
              stroke={headerTheme.accent_color_secondary} strokeWidth="3" fill="none" strokeLinecap="round" />
            <path d="M380,0 C360,50 340,90 355,150 C370,210 320,260 340,320" 
              stroke={headerTheme.accent_color || '#3EC9E0'} strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.6" />
          </svg>
        </div>
      )}
      
      {/* Subtle loading indicator - only shows when media is loading */}
      {mediaLoading && !loading && (
        <div className="absolute top-4 right-4 z-20 bg-black/50 backdrop-blur-sm rounded-full p-2 shadow-lg">
          <Loader2 className="w-5 h-5 text-white animate-spin" />
        </div>
      )}
      
      {/* Content - only show when theme is loaded */}
      <div className={`relative z-10 text-center text-white px-4 max-w-5xl mx-auto`}>
        {isThemeLoaded && finalTitle && (
          <div className="transition-opacity duration-500 opacity-100">
            <h1 
              className={`text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight`}
              style={{ color: headerTheme?.title_color || (textColor?.startsWith?.('#') ? textColor : '#ffffff') }}
            >
              {finalTitle}
              {finalSubtitle && (
                <>
                  <br />
                  <span 
                    className={`text-xl sm:text-2xl font-normal`}
                    style={{ color: headerTheme?.subtitle_color || '#06b6d4' }}
                  >
                    {finalSubtitle}
                  </span>
                </>
              )}
            </h1>
            {finalDescription && (
              <p 
                className="text-lg sm:text-xl mb-8 max-w-3xl mx-auto leading-relaxed"
                style={{ color: headerTheme?.description_color || '#ffffff' }}
              >
                {finalDescription}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Scroll indicator — double chevrons with glow */}
      {showScrollArrow && !loading && !scrollHidden && (
        <div 
          className="absolute bottom-10 left-1/2 flex flex-col items-center cursor-pointer transition-all duration-700 ease-out"
          style={{ opacity: scrollOpacity, transform: `translateX(-50%) translateY(${(1 - scrollOpacity) * 12}px)` }}
          onClick={() => window.scrollBy({ top: window.innerHeight * 0.8, behavior: 'smooth' })}
          data-testid="scroll-indicator"
        >
          <span className="text-white/90 text-xs tracking-[0.35em] uppercase font-semibold mb-3" style={{ textShadow: '0 1px 12px rgba(0,0,0,0.6)' }}>Scroll</span>
          <div className="flex flex-col items-center" style={{ filter: 'drop-shadow(0 0 10px rgba(162,133,212,0.4))' }}>
            <svg width="32" height="18" viewBox="0 0 32 18" fill="none" className="animate-bounce" style={{ animationDuration: '2s' }}>
              <path d="M2 2L16 14L30 2" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.9"/>
            </svg>
            <svg width="32" height="18" viewBox="0 0 32 18" fill="none" className="animate-bounce -mt-2" style={{ animationDuration: '2s', animationDelay: '0.15s' }}>
              <path d="M2 2L16 14L30 2" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
            </svg>
          </div>
        </div>
      )}
    </section>
  );
};

export default HeroSection;
